import dayjs from 'dayjs';
import {
  geocodeByAddress,
  geocodeByPlaceId,
  geocodeByLatLng,
} from 'react-google-places-autocomplete';
import { FieldValues } from 'react-hook-form';
import { UseTranslationResponse } from 'react-i18next';
import { UserPermission, MultiStopLegsSchemaType } from 'remote/global';
import {
  QueryFilter,
  OfferLeg,
  DriverCandidate,
  OfferLocation,
  DaysOfWeek,
} from './data/data-types';
import { featureFlags } from '../configs/flag-config';
import Config from '../configs/env-config';

export const isLocal = window.MOBIFER_ENV === 'local';
export const isStaging = window.MOBIFER_ENV === 'dev';
export const isDev = isLocal || isStaging;
export const isProd = window.MOBIFER_ENV === 'prod';

export const isOnlyDev = ['dev', 'local'].includes(Config.ENV);

const getTimeZoneConvertedString = (date: Date) => {
  const convertedDate = dayjs(date).tz(dayjs.tz.guess()).format('YYYY-MM-DD');
  return convertedDate;
};

const getGeocodedValues = async (val: any): Promise<OfferLocation | null> => {
  const geocoded = await geocodeByPlaceId(val?.place_id);
  if (geocoded?.length) {
    const locationText = geocoded[0]?.formatted_address;
    const placeId = geocoded[0]?.place_id;
    const coords = {
      lat: geocoded[0]?.geometry?.location?.lat(),
      lon: geocoded[0]?.geometry?.location?.lng(),
    };

    return {
      address: locationText,
      locationText,
      placeId,
      coords,
    };
  }
  return null;
};

const getPlaceIdByAddress = async (address: string): Promise<string> => {
  const geocode = await geocodeByAddress(address);
  if (geocode.length) return geocode[0].place_id;

  return '';
};

const getDaysOfWeekOptions = (tInstance: UseTranslationResponse<'translation'>) => {
  const { t } = tInstance;

  return Object.values(DaysOfWeek).map((value) => ({
    label: t(`DaysOfWeek.${value}`),
    value,
  }));
};

const getDaysOfWeekLabels = (
  value: FieldValues,
  tInstance: UseTranslationResponse<'translation'>
) => {
  const { t, i18n } = tInstance;
  const opts = value?.map((v: string) =>
    t(`DaysOfWeek.${v}`).slice(0, i18n.language === 'et' ? 1 : 2)
  );
  return opts?.length > 0 ? opts.join(', ') : t('DaysOfWeek.select');
};

const getGeocodedValuesByLatLng = async (lat: number, lng: number) => {
  const results = await geocodeByLatLng({ lat, lng });
  if (!results) return null;

  const locationText = results[0]?.formatted_address;
  const placeId = results[0]?.place_id;
  const parsedValue = {
    address: locationText,
    placeId,
    locationText,
    coords: {
      lat,
      lon: lng,
    },
  };
  return parsedValue;
};

const isDateBeforeAnotherDate = (beforeDate: Date | string, afterDate: Date | string) => {
  return dayjs(beforeDate).isBefore(dayjs(afterDate));
};

const isTimeBeforeAnotherTime = (beforeTime: string, afterTime: string) => {
  return dayjs(beforeTime, 'HH:mm').isBefore(dayjs(afterTime, 'HH:mm'));
};

const getModifiedCandidatesArray = (
  driverCandidates: DriverCandidate[],
  currentValue: { value: string; id: number }
) => {
  let modifiedArr = driverCandidates;
  const modifiedCandidatesArray = driverCandidates.filter(
    (candidate: DriverCandidate) => candidate.value === currentValue.value
  );
  if (modifiedCandidatesArray.length === 0) {
    const idExists = modifiedArr.findIndex(
      (candidate: DriverCandidate) => candidate.id === currentValue.id
    );
    if (idExists !== -1) return driverCandidates;

    modifiedArr = [...driverCandidates];
    if (currentValue.id !== 0) {
      modifiedArr.push({
        value: currentValue.value,
        id: currentValue.id,
      });
    }
  }
  return modifiedArr;
};

const getInitialValues = (arr: any[]) =>
  arr.reduce(
    (acc, current) => ({
      ...acc,
      [current.key]: current.initialValue,
    }),
    {}
  );

const getParsedMultiStopLegs = async (
  legs: MultiStopLegsSchemaType[]
): Promise<OfferLeg[] | []> => {
  const multiStopLocations = await Promise.all(
    legs.map((leg: MultiStopLegsSchemaType) => getGeocodedValues((leg as any).location))
  );

  const parsedLegs = legs.map((leg: MultiStopLegsSchemaType, index: number) => ({
    startTime: (leg as any).startTime,
    startLocation: multiStopLocations[index],
    endLocation: multiStopLocations[index + 1],
  }));
  parsedLegs.pop();
  return parsedLegs;
};

const getQueryString = (filter?: QueryFilter) => {
  if (!filter) {
    return '';
  }

  let queryString = '?';

  Object.keys(filter).forEach((key) => {
    if (key !== 'sortableProperties') {
      queryString = queryString.concat(`${key}=${filter[key as keyof typeof filter]}&`);
    }
  });

  queryString = queryString.slice(0, -1);

  if (queryString.includes('#')) {
    queryString = queryString.replace('#', '');
  }

  return queryString;
};

const getParsedLocationValues = async (placeId: string) => {
  const results = await geocodeByPlaceId(placeId);
  const newAddress = results[0]?.formatted_address;
  const lat = results[0]?.geometry.location.lat();
  const lon = results[0]?.geometry.location.lng();
  const countryIndex = results[0]?.address_components.findIndex(
    ({ types }) => types?.length && types[0] === 'country'
  );
  const countryCode = results[0]?.address_components[countryIndex].short_name;
  const parsedLocation = {
    address: newAddress,
    coords: {
      lat,
      lon,
    },
    countryCode,
  };
  return parsedLocation;
};

const getDriverIdFromSelect = (obj: any): number => {
  const index = obj?.key.indexOf('$');
  const id = Number(obj?.key.substring(index + 1));
  return id;
};

const hasReadOnlyPermission = (permission: String) => {
  const { READ_OWN, READ_ALL } = UserPermission;
  return permission === READ_OWN || permission === READ_ALL;
};

const hasWritePermission = (permission: String) => {
  const { WRITE_OWN, WRITE_ALL } = UserPermission;
  return permission === WRITE_OWN || permission === WRITE_ALL;
};

const isFeatureEnabled = (featureName: String) => {
  return (featureFlags as any)
    ?.find((f: any) => featureName === f.name)
    ?.environments?.find((e: any) => Config.ENV === e.name)?.isFeatureEnabled;
};

// Creates a url with a https prefix if it does not exist in the string
// If the url is already prefixed with either http or https, it will use the one provided
const createUrlWithProtocol = (url: string) =>
  url.replace(/^(?:(.*:)?\/\/)?(.*)/i, (match, schema, _url) =>
    schema ? match : `https://${_url}`
  );

function roundRemainderDate(date: Date, minutes = 5) {
  const start = dayjs(date);
  // Round up the number to the nearest minutes
  const remainder = minutes - (start.minute() % minutes);
  const result = dayjs(date).add(remainder, 'minutes');
  return result;
}

// eslint-disable-next-line consistent-return
const getUrlByProject = (project: 'landing' | 'operator' | 'admin' | 'corporate') => {
  switch (project) {
    case 'landing':
      if (isDev) return `https://landing.dev.mobifer.com`;
      return `https://mobifer.com`;
    case 'operator':
      if (isDev) return `https://operator.dev.mobifer.com`;
      return `https://operator.mobifer.com`;
    case 'admin':
      if (isDev) return `https://admin.dev.mobifer.com`;
      return `https://admin.mobifer.com`;
    case 'corporate':
      if (isDev) return `https://corporate.dev.mobifer.com`;
      return `https://corporate.mobifer.com`;
    default:
  }
};

function findClosestMatch(
  dateRanges: Record<any, any>[],
  targetDate: Date
): Record<any, any> | null {
  if (dateRanges.length === 0) {
    return null;
  }

  let closestRange: Record<any, any> | null = null;
  let closestDistance = Number.MAX_SAFE_INTEGER;

  // Make sure the targetDate falls between dateRange.start and dateRange.end
  const filteredRanges = dateRanges.filter((dateRange) => {
    return (
      targetDate >= new Date(dateRange.start as string) &&
      targetDate <= new Date(dateRange.end as string)
    );
  });

  filteredRanges.forEach((dateRange) => {
    const startDistance = Math.abs(
      targetDate.getTime() - new Date(dateRange.start as string).getTime()
    );
    const endDistance = Math.abs(
      targetDate.getTime() - new Date(dateRange.end as string).getTime()
    );

    if (startDistance < closestDistance) {
      closestRange = dateRange;
      closestDistance = startDistance;
    }

    if (endDistance < closestDistance) {
      closestRange = dateRange;
      closestDistance = endDistance;
    }
  });

  return closestRange;
}

const getMonthViewRange = (date: Date) => {
  let yesterday = dayjs(date).startOf('month');
  let future = dayjs(date).endOf('month');

  // If the date is in the current month, we mark yesterday and end of month days
  if (dayjs().isSame(date, 'year') && dayjs().isSame(date, 'month')) {
    yesterday = dayjs().subtract(1, 'day');
    future = dayjs().add(dayjs().daysInMonth(), 'days');
  }

  return {
    start: yesterday.toISOString(),
    end: future.toISOString(),
  };
};

const getCustomWeekViewRange = (date: Date) => {
  const yesterday = dayjs(date).subtract(1, 'day');
  const future = dayjs(date).endOf('week');

  return {
    start: yesterday.toISOString(),
    end: future.toISOString(),
  };
};

const triggerDownloadLink = (blob: Blob, fileName: string) => {
  const href = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

function splitArrayAtMiddle(inputArray: any[]): any[][] {
  const middleIndex = Math.floor(inputArray.length / 2);
  const firstHalf = inputArray.slice(0, middleIndex);
  const secondHalf = inputArray.slice(middleIndex);

  return [firstHalf, secondHalf];
}

const forEachSeries = async (iterable: any[], action: Function) => {
  await iterable.reduce(async (previousPromise, x) => {
    await previousPromise;
    await action(x);
  }, Promise.resolve());
};

function parseFormattedText(text: string, matches: any[][]) {
  const result = [];

  if (matches.length === 0) {
    result.push({
      text,
      highlight: false,
    });
  } else if (matches[0][0] > 0) {
    result.push({
      text: text.slice(0, matches[0][0]),
      highlight: false,
    });
  }

  matches.forEach((match, i) => {
    const startIndex = match[0];
    const endIndex = match[1];

    result.push({
      text: text.slice(startIndex, endIndex),
      highlight: true,
    });

    if (i === matches.length - 1) {
      if (endIndex < text.length) {
        result.push({
          text: text.slice(endIndex, text.length),
          highlight: false,
        });
      }
    } else if (endIndex < matches[i + 1][0]) {
      result.push({
        text: text.slice(endIndex, matches[i + 1][0]),
        highlight: false,
      });
    }
  });

  return result;
}

async function runSequentially<T>(promiseFuncs: (() => Promise<T>)[], cb?: (result: T) => void) {
  return promiseFuncs.reduce(async (prevPromise, currPromiseFunc) => {
    await prevPromise;
    const result = await currPromiseFunc();
    cb?.(result);
  }, Promise.resolve());
}

export function formatDateWithTz(date?: Date) {
  if (date == null) return undefined;
  return dayjs(date).tz(Intl.DateTimeFormat().resolvedOptions().timeZone, true).toISOString();
}

export {
  getParsedLocationValues,
  getModifiedCandidatesArray,
  getQueryString,
  getDriverIdFromSelect,
  getInitialValues,
  hasReadOnlyPermission,
  hasWritePermission,
  isFeatureEnabled,
  getTimeZoneConvertedString,
  getGeocodedValues,
  getParsedMultiStopLegs,
  createUrlWithProtocol,
  roundRemainderDate,
  getUrlByProject,
  findClosestMatch,
  isDateBeforeAnotherDate,
  isTimeBeforeAnotherTime,
  getPlaceIdByAddress,
  getMonthViewRange,
  getCustomWeekViewRange,
  triggerDownloadLink,
  splitArrayAtMiddle,
  forEachSeries,
  parseFormattedText,
  getGeocodedValuesByLatLng,
  getDaysOfWeekLabels,
  getDaysOfWeekOptions,
  runSequentially,
};
